import { type ReactNode } from 'react';
import styled, { css } from 'styled-components';

import BackChevronIconSvg from '../../../assets/icons/icon-back-chevron.svg?react';
import {
  BottomNavPageLayout,
  HomeDashboardBottomNav,
  BaseButton,
  rubikTypographyStyles,
} from '../../../shared/ui';

type MyPageSettingsPageLayoutProps = {
  backAriaLabel: string;
  children: ReactNode;
  frameDataUi: string;
  headerAction?: ReactNode;
  onBack: () => void;
  onOpenHome: () => void;
  onOpenMyPage: () => void;
  overlay?: ReactNode;
  pageDataUi: string;
  title: string;
};

const FRAME_TOP_PADDING_PX = 20;
const FRAME_SIDE_PADDING_PX = 28;
const FOOTER_TOP_PADDING_PX = 10;
const FOOTER_BOTTOM_PADDING_PX = 13;

const edgePaddingStyles = css`
  padding-right: ${({ theme }) =>
    `max(${theme.scale.px(
      FRAME_SIDE_PADDING_PX,
    )}, env(safe-area-inset-right))`};
  padding-left: ${({ theme }) =>
    `max(${theme.scale.px(FRAME_SIDE_PADDING_PX)}, env(safe-area-inset-left))`};
`;

const Screen = styled.div.attrs({
  'data-ui': 'my-page-settings-screen',
})`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding-top: ${({ theme }) =>
    `max(${theme.scale.px(FRAME_TOP_PADDING_PX)}, env(safe-area-inset-top))`};
  ${edgePaddingStyles};
`;

const Header = styled.header.attrs({
  'data-ui': 'my-page-settings-header',
})`
  min-height: ${({ theme }) => theme.scale.px(56)};
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  column-gap: ${({ theme }) => theme.scale.px(15)};
`;

const HeaderSideButton = styled(BaseButton)`
  width: ${({ theme }) => theme.scale.px(24)};
  height: ${({ theme }) => theme.scale.px(24)};
  display: grid;
  place-items: center;
`;

const HeaderSideSlot = styled.div`
  width: ${({ theme }) => theme.scale.px(24)};
  height: ${({ theme }) => theme.scale.px(24)};
`;

const BackIcon = styled(BackChevronIconSvg).attrs({
  'aria-hidden': true,
  focusable: 'false',
})`
  width: ${({ theme }) => theme.scale.px(12)};
  height: ${({ theme }) => theme.scale.px(20)};
  color: ${({ theme }) => theme.colors.text.primary};

  & path {
    stroke: currentColor;
  }
`;

const Title = styled.h1`
  ${rubikTypographyStyles};
  min-width: 0;
  margin: 0;
  font-size: ${({ theme }) => theme.scale.px(24)};
  font-weight: 600;
  line-height: ${({ theme }) => theme.scale.px(24)};
`;

const Body = styled.div.attrs({
  'data-ui': 'my-page-settings-body',
})`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding-top: ${({ theme }) => theme.scale.px(10)};
  padding-bottom: ${({ theme }) => theme.scale.px(16)};
`;

const BottomNavSlot = styled.div.attrs({
  'data-ui': 'my-page-settings-bottom-nav-slot',
})`
  flex-shrink: 0;
  display: grid;
  justify-items: center;
  padding-top: ${({ theme }) => theme.scale.px(FOOTER_TOP_PADDING_PX)};
  padding-right: ${({ theme }) =>
    `max(${theme.scale.px(
      FRAME_SIDE_PADDING_PX,
    )}, env(safe-area-inset-right))`};
  padding-bottom: ${({ theme }) =>
    `max(${theme.scale.px(
      FOOTER_BOTTOM_PADDING_PX,
    )}, env(safe-area-inset-bottom))`};
  padding-left: ${({ theme }) =>
    `max(${theme.scale.px(FRAME_SIDE_PADDING_PX)}, env(safe-area-inset-left))`};
`;

export function MyPageSettingsPageLayout({
  backAriaLabel,
  children,
  frameDataUi,
  headerAction,
  onBack,
  onOpenHome,
  onOpenMyPage,
  overlay,
  pageDataUi,
  title,
}: MyPageSettingsPageLayoutProps) {
  const resolvedHeaderAction = headerAction ?? <HeaderSideSlot aria-hidden />;

  return (
    <BottomNavPageLayout
      frameDataUi={frameDataUi}
      overlay={overlay}
      pageDataUi={pageDataUi}
      stickyFooter={
        <BottomNavSlot>
          <HomeDashboardBottomNav
            activeTab={null}
            onOpenHome={onOpenHome}
            onOpenMyPage={onOpenMyPage}
          />
        </BottomNavSlot>
      }
    >
      <Screen>
        <Header>
          <HeaderSideButton
            type="button"
            aria-label={backAriaLabel}
            onClick={onBack}
          >
            <BackIcon />
          </HeaderSideButton>
          <Title>{title}</Title>
          {resolvedHeaderAction}
        </Header>

        <Body>{children}</Body>
      </Screen>
    </BottomNavPageLayout>
  );
}
